import {useQueryClient} from '@tanstack/react-query';
import {useCallback} from 'react';
import {
  useMinIoFileStorageServiceDeleteApiFilesBulk,
  useMinIoFileStorageServiceDeleteApiFilesByFilename,
  useMinIoFileStorageServicePatchApiFilesBulkStatus,
  useMinIoFileStorageServicePatchApiFilesByFilenameStatus,
  useMinIoFileStorageServicePostApiFilesUpload
} from '../../api/generated/queries/queries';
import {FileMetadataDTO} from '../../api/generated/requests/types.gen';
import {MinIoFileStorageService} from '../../api/generated/requests/services.gen';
import {useSnackbar} from 'notistack';

/**
 * Hook for file write operations (mutations) extracted from useFileData
 * Handles upload, delete, restore, download, and bulk operations
 */
export const useFileMutations = () => {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  // Cache management functions (duplicated from useFileQuery for mutations)
  const updateFileInCache = (fileId: string, updates: Partial<FileMetadataDTO>) => {
    queryClient.setQueryData(
      ['files'],
      (oldData: FileMetadataDTO[] | undefined) => {
        if (!oldData) return oldData;
        return oldData.map(file => 
          file.filename === fileId ? { ...file, ...updates } : file
        );
      }
    );
  };

  const addFileToCache = (newFile: FileMetadataDTO) => {
    queryClient.setQueryData(
      ['files'],
      (oldData: FileMetadataDTO[] | undefined) => {
        return oldData ? [newFile, ...oldData] : [newFile];
      }
    );
  };

  const removeFileFromCache = (filename: string) => {
    queryClient.setQueryData(
      ['files'],
      (oldData: FileMetadataDTO[] | undefined) => {
        if (!oldData) return oldData;
        return oldData.filter(file => file.filename !== filename);
      }
    );
    
    // Odśwież cache dla głównej listy plików
    queryClient.invalidateQueries({ 
      queryKey: ['files'] 
    });
    
    // Odśwież cache dla plików powiązanych z obiektami
    queryClient.invalidateQueries({ 
      queryKey: ['MinIoFileStorageServiceGetApiFilesByRelatedObjectByIdByType'] 
    });
  };

  // Helper function to create optimistic file
  const createOptimisticFile = (file: File): FileMetadataDTO => {
    return {
      filename: `temp_${Date.now()}_${file.name}`,
      originalFilename: file.name,
      contentType: file.type,
      fileSize: file.size,
      status: 'UPLOADING' as any,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    } as FileMetadataDTO;
  };

  // Helper function to get file display status as mentioned in issue #15
  const getFileDisplayStatus = (file: FileMetadataDTO) => {
    // Use type assertion for extended status values
    const extendedStatus = file.status as 'ACTIVE' | 'DETACHED' | 'UPLOADING' | 'ERROR' | undefined;
    if (extendedStatus === 'UPLOADING') return 'uploading';
    if (extendedStatus === 'ERROR') return 'error';
    if (!file.contentType) return 'processing';
    return 'ready';
  };

  // Upload mutation with proper optimistic updates and cache invalidation
  const uploadMutation = useMinIoFileStorageServicePostApiFilesUpload({
    onMutate: async (variables) => {
      // Cancel any outgoing refetches (so they don't overwrite our optimistic update)
      await queryClient.cancelQueries({ queryKey: ['files'] });
      
      // Snapshot the previous value
      const previousFiles = queryClient.getQueryData(['files']);
      
      // Extract file from formData
      const file = variables.formData?.file;
      if (file instanceof File) {
        // Optimistically update to the new value with complete metadata
        const optimisticFile = createOptimisticFile(file);
        queryClient.setQueryData(
          ['files'],
          (oldData: FileMetadataDTO[] | undefined) => {
            return oldData ? [optimisticFile, ...oldData] : [optimisticFile];
          }
        );
      }
      
      // Return a context object with the snapshotted value
      return { previousFiles, tempFilename: file instanceof File ? `temp_${Date.now()}_${file.name}` : null };
    },
    onSuccess: async (data: any, variables, context) => {
      if (data) {
        // Remove optimistic file and add real file data
        if (context?.tempFilename) {
          queryClient.setQueryData(
            ['files'],
            (oldData: FileMetadataDTO[] | undefined) => {
              if (!oldData) return [data];
              // Remove optimistic file and add real file
              const withoutOptimistic = oldData.filter(file => file.filename !== context.tempFilename);
              return [data, ...withoutOptimistic];
            }
          );
        }
        
        // Invalidate and refetch queries to ensure consistency
        await queryClient.invalidateQueries({ queryKey: ['files'] });
        await queryClient.refetchQueries({ queryKey: ['files'], type: 'active' });
        
        enqueueSnackbar('Plik został pomyślnie przesłany', { variant: 'success' });
      }
    },
    onError: (error: any, variables, context) => {
      // Update optimistic file status to ERROR instead of rollback
      if (context?.tempFilename) {
        queryClient.setQueryData(
          ['files'],
          (oldData: FileMetadataDTO[] | undefined) => {
            if (!oldData) return oldData;
            return oldData.map(file => 
              file.filename === context.tempFilename 
                ? { ...file, status: 'ERROR' as any }
                : file
            );
          }
        );
      } else if (context?.previousFiles) {
        // Fallback to rollback if no temp filename
        queryClient.setQueryData(['files'], context.previousFiles);
      }
      
      console.error('Upload failed:', error);
      enqueueSnackbar('Błąd podczas przesyłania pliku', { variant: 'error' });
    }
  });

  // Retry mechanism for failed uploads as mentioned in issue #16
  const retryFailedUploads = useCallback(async (files: FileMetadataDTO[]) => {
    const failedFiles = files.filter(f => {
      const extendedStatus = f.status as 'ACTIVE' | 'DETACHED' | 'UPLOADING' | 'ERROR' | undefined;
      return extendedStatus === 'ERROR';
    });
    
    for (const file of failedFiles) {
      if (file.originalFilename) {
        try {
          // Create a new File object for retry (this is a simplified approach)
          // In a real implementation, you'd need to store the original File object
          const blob = new Blob([''], { type: file.contentType || 'application/octet-stream' });
          const retryFile = new File([blob], file.originalFilename, { 
            type: file.contentType || 'application/octet-stream' 
          });
          
          await uploadMutation.mutateAsync({
            formData: { file: retryFile }
          });
        } catch (error) {
          console.error(`Retry failed for file ${file.originalFilename}:`, error);
        }
      }
    }
  }, [uploadMutation]);

  // Delete mutation
  const deleteMutation = useMinIoFileStorageServiceDeleteApiFilesByFilename({
    onSuccess: (data: any, variables: any) => {
      removeFileFromCache(variables.filename);
      enqueueSnackbar('Plik został usunięty', { variant: 'success' });
    },
    onError: (error: any) => {
      console.error('Delete failed:', error);
      enqueueSnackbar('Błąd podczas usuwania pliku', { variant: 'error' });
    }
  });

  // Status change mutation (for move to trash/restore)
  const statusChangeMutation = useMinIoFileStorageServicePatchApiFilesByFilenameStatus({
    onSuccess: async (data: any, variables: any) => {
      updateFileInCache(variables.filename, { 
        status: variables.status 
      });
      
      // Invalidate and refetch queries to ensure immediate UI refresh
      await queryClient.invalidateQueries({ queryKey: ['files'] });
      await queryClient.refetchQueries({ queryKey: ['files'], type: 'active' });
      
      const message = variables.status === 'DETACHED' 
        ? 'Plik został przeniesiony do kosza' 
        : 'Plik został przywrócony';
      enqueueSnackbar(message, { variant: 'success' });
    },
    onError: (error: any, variables: any) => {
      console.error('Status change failed:', error);
      const message = variables.status === 'DETACHED' 
        ? 'Błąd podczas przenoszenia do kosza' 
        : 'Błąd podczas przywracania pliku';
      enqueueSnackbar(message, { variant: 'error' });
    }
  });

  // Bulk status change mutation
  const bulkStatusChangeMutation = useMinIoFileStorageServicePatchApiFilesBulkStatus({
    onSuccess: async (data: any, variables: any) => {
      variables.requestBody.forEach((filename: string) => {
        updateFileInCache(filename, { status: variables.status });
      });
      
      // Invalidate and refetch queries to ensure immediate UI refresh
      await queryClient.invalidateQueries({ queryKey: ['files'] });
      await queryClient.refetchQueries({ queryKey: ['files'], type: 'active' });
      
      const message = variables.status === 'DETACHED' 
        ? 'Pliki zostały przeniesione do kosza' 
        : 'Pliki zostały przywrócone';
      enqueueSnackbar(message, { variant: 'success' });
    },
    onError: (error: any, variables: any) => {
      console.error('Bulk status change failed:', error);
      const message = variables.status === 'DETACHED' 
        ? 'Błąd podczas przenoszenia plików do kosza' 
        : 'Błąd podczas przywracania plików';
      enqueueSnackbar(message, { variant: 'error' });
    }
  });

  // Bulk delete mutation
  const bulkDeleteMutation = useMinIoFileStorageServiceDeleteApiFilesBulk({
    onSuccess: (data: any, variables: any) => {
      variables.requestBody.forEach((filename: string) => {
        removeFileFromCache(filename);
      });
      enqueueSnackbar('Pliki zostały usunięte', { variant: 'success' });
    },
    onError: (error: any) => {
      console.error('Bulk delete failed:', error);
      enqueueSnackbar('Błąd podczas usuwania plików', { variant: 'error' });
    }
  });

  // Helper functions
  const handleFileDelete = (file: FileMetadataDTO) => {
    if (!file.filename) return;
    
    if (file.status === 'DETACHED') {
      // Permanent delete
      deleteMutation.mutate({
        filename: file.filename
      });
    } else {
      // Move to trash
      statusChangeMutation.mutate({
        filename: file.filename,
        status: 'DETACHED'
      });
    }
  };

  const handleFileRestore = (file: FileMetadataDTO) => {
    if (!file.filename) return;
    
    statusChangeMutation.mutate({
      filename: file.filename,
      status: 'ACTIVE'
    });
  };

  const downloadFile = async (filename: string, originalFilename?: string) => {
    try {
      const response = await MinIoFileStorageService.getApiFilesByFilenameDownload({
        filename
      });

      // The response contains downloadUrl
      const downloadUrl = (response as any).downloadUrl;
      if (!downloadUrl) {
        throw new Error('No download URL in response');
      }

      // Fetch the file as blob to force download instead of opening in browser
      const fileResponse = await fetch(downloadUrl);
      if (!fileResponse.ok) {
        throw new Error(`HTTP error! status: ${fileResponse.status}`);
      }

      const blob = await fileResponse.blob();
      const blobUrl = window.URL.createObjectURL(blob);

      // Create a link element and trigger download
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = originalFilename || filename;
      
      try {
        document.body.appendChild(link);
        link.click();
      } finally {
        document.body.removeChild(link);
        // Clean up the blob URL to prevent memory leaks
        window.URL.revokeObjectURL(blobUrl);
      }
    } catch (error) {
      console.error('Download failed:', error);
      enqueueSnackbar('Błąd podczas pobierania pliku', { variant: 'error' });
    }
  };

  const handleBulkStatusChange = (filenames: string[], status: 'ACTIVE' | 'DETACHED') => {
    bulkStatusChangeMutation.mutate({
      requestBody: filenames,
      status
    });
  };

  const handleBulkDelete = (filenames: string[]) => {
    bulkDeleteMutation.mutate({
      requestBody: filenames
    });
  };

  return {
    // Upload
    uploadFile: uploadMutation.mutate,
    isUploading: uploadMutation.isPending,
    
    // Delete
    deleteFile: handleFileDelete,
    isDeleting: deleteMutation.isPending,
    
    // Restore
    restoreFile: handleFileRestore,
    isChangingStatus: statusChangeMutation.isPending,
    
    // Download
    downloadFile,
    
    // Bulk operations
    bulkStatusChange: handleBulkStatusChange,
    bulkDelete: handleBulkDelete,
    isBulkChangingStatus: bulkStatusChangeMutation.isPending,
    isBulkDeleting: bulkDeleteMutation.isPending,
    
    // File status and retry
    getFileDisplayStatus,
    retryFailedUploads,
    
    // Cache management
    updateFileInCache,
    addFileToCache,
    removeFileFromCache,
  };
};